import React, { useLayoutEffect, useRef, useState } from "react";
import BioDisclosure from "@/components/BioDisclosure";
import BioLeadForm from "@/components/BioLeadForm";
import { compactPrice } from "@/helpers/compactPrice";
import "./AgentBioHeader.scss";

export const bioStats = (agent) => {
  const active = Number(agent?.activeWantAdCount) || (agent?.activeWantAds || []).length;
  const activeDemand = (agent?.activeWantAds || []).reduce(
    (sum, wantAd) => sum + (Number(wantAd?.Budget_mx) || 0),
    0,
  );
  const history = agent?.history || {};
  return [
    active ? { key: "active", value: active, label: active === 1 ? "Active buyer" : "Active buyers" } : null,
    activeDemand ? { key: "activeDemand", value: compactPrice(activeDemand), label: "Active demand" } : null,
    Number(history.total)
      ? {
          key: "allWantListings",
          value: Number(history.total),
          label: `All-time Buyer\u00a0Want\u2060-\u2060${Number(history.total) === 1 ? "Listing" : "Listings"}`,
        }
      : null,
    history.totalDemand
      ? { key: "allDemand", value: compactPrice(history.totalDemand), label: "All-time demand" }
      : null,
  ].filter(Boolean);
};

const initialsOf = (firstName, lastName) =>
  `${(firstName || "").trim().charAt(0)}${(lastName || "").trim().charAt(0)}`.toUpperCase();

const ICONS = {
  call: "M6.6 3.5h2.6l1.3 3.6-1.8 1.2a10 10 0 0 0 5 5l1.2-1.8 3.6 1.3v2.6a2 2 0 0 1-2.1 2A14.5 14.5 0 0 1 4.6 5.6a2 2 0 0 1 2-2.1z",
  text: "M4.5 5.5h15v10h-9l-4.5 3.5v-3.5h-1.5z",
  message: "M20 4 3.5 10.5l6.5 2.5L20 4l-7 16-3-7",
};

const ContactIcon = ({ name }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d={ICONS[name]} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" strokeLinecap="round" />
  </svg>
);

const AgentBioHeader = ({
  agent,
  isGitchaWebsite,
  onDirectMessage,
  onBuyerListingForm,
  proBadge,
  onTrack,
}) => {
  const [failedImage, setFailedImage] = useState(null);
  const [failedLogo, setFailedLogo] = useState(null);
  const [bioOpen, setBioOpen] = useState(false);
  const [bioOverflows, setBioOverflows] = useState(false);
  const bioRef = useRef(null);

  useLayoutEffect(() => {
    const node = bioRef.current;
    if (!node || bioOpen) return undefined;
    const measure = () => setBioOverflows(node.scrollHeight > node.clientHeight + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [agent?.Bio, agent?.isPremiumAgent, bioOpen]);

  if (!agent) return null;

  const isPro = Boolean(agent.isPremiumAgent);
  const fullName = [agent.FirstName, agent.LastName].filter(Boolean).join(" ");
  const showPhoto = isPro && Boolean(agent.Img) && agent.Img !== failedImage;
  const company = (agent.Company || "").trim();
  const phone = (agent.Phone || "").trim();
  const stats = bioStats(agent);

  const showInlineForm = isPro && isGitchaWebsite;
  const message = { key: "message", label: "Message", icon: "message", onClick: onDirectMessage };
  const call = phone ? { key: "call", label: "Call", icon: "call", href: `tel:${phone}`, track: { type: "call" } } : null;
  const text = phone ? { key: "text", label: "Text", icon: "text", href: `sms:${phone}`, track: { type: "text" } } : null;
  const primary = showInlineForm
    ? null
    : isPro
      ? { key: "form", label: "Buyer Listing Form", onClick: onBuyerListingForm }
      : { key: "message", label: `Message ${agent.FirstName || ""}`.trim(), onClick: onDirectMessage };
  const quiet = [call, text, primary?.key === "message" ? null : message].filter(Boolean);

  const renderQuiet = (action) => {
    const inner = (
      <>
        <ContactIcon name={action.icon} />
        {action.label}
      </>
    );
    return action.href ? (
      <a
        key={action.key}
        className="agent-bio__contact"
        href={action.href}
        onClick={action.track && onTrack ? () => onTrack(action.track) : undefined}
      >
        {inner}
      </a>
    ) : (
      <button key={action.key} type="button" className="agent-bio__contact" onClick={action.onClick}>
        {inner}
      </button>
    );
  };

  return (
    <section className="agent-bio">
      <div className="agent-bio__identity-col">
        <div className={`agent-bio__avatar-wrap${proBadge && isPro ? " is-pro" : ""}`}>
          <div className="agent-bio__avatar">
            {showPhoto ? (
              <img src={agent.Img} alt={fullName} onError={() => setFailedImage(agent.Img)} />
            ) : (
              <span aria-hidden="true">{initialsOf(agent.FirstName, agent.LastName)}</span>
            )}
          </div>
          {proBadge && isPro ? <span className="agent-bio__pro-tab">PRO</span> : null}
        </div>
        <h1 className="agent-bio__name">{fullName}</h1>
        {proBadge && isPro ? <p className="agent-bio__pro-line">Gitcha Pro agent</p> : null}
        <BioDisclosure agent={agent} />
        {agent.team?.name ? <p className="agent-bio__team">Team: {agent.team.name}</p> : null}
        {isPro && agent.logoUrl && agent.logoUrl !== failedLogo ? (
          <img
            className="agent-bio__logo"
            src={agent.logoUrl}
            alt={company || "Brokerage logo"}
            onError={() => setFailedLogo(agent.logoUrl)}
          />
        ) : null}
        {isPro && agent.Bio ? (
          <div className="agent-bio__bio">
            <p ref={bioRef} className={bioOpen ? "" : "agent-bio__bio-text--clamped"}>
              {agent.Bio}
            </p>
            {bioOverflows || bioOpen ? (
              <button type="button" className="agent-bio__more" onClick={() => setBioOpen(!bioOpen)}>
                {bioOpen ? "Show less" : "Read more"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <aside className="agent-bio__card">
        {stats.length ? (
          <dl className="agent-bio__stats">
            {stats.map((stat) => (
              <div key={stat.key} className="agent-bio__stat">
                <dt>{stat.label}</dt>
                <dd>{stat.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}
        {showInlineForm ? (
          <BioLeadForm inline slug={agent.slug} firstName={agent.FirstName} />
        ) : null}
        {primary ? (
          <button type="button" className="agent-bio__cta" onClick={primary.onClick}>
            {primary.label}
          </button>
        ) : null}
        {quiet.length ? (
          <div className="agent-bio__contacts" style={{ "--contact-count": quiet.length }}>
            {quiet.map(renderQuiet)}
          </div>
        ) : null}
      </aside>

    </section>
  );
};

export default AgentBioHeader;
